import { useMsal } from "@azure/msal-react";
import React from "react";
import { useDispatch } from "react-redux";
import MicrosoftIcon from "../../../../assests/icons/MicrosoftIcon";
import { loginRequest } from "../../../../config/msal";
import { routes, tokenIdf } from "../../../../data/constants";
import { useNavigation } from "../../../../hooks/useNavigation";
import { loginWithAzure } from "../../../../services/azure";
import { setLocalItem } from "../../../../utils/localStorageUtils";
import Notifications from "../../../../utils/notifications";
import { Button, CircularProgress, Typography } from "../helper";

const useStyle = () => ({
  typographyStyle: () => ({ fontSize: 14, fontWeight: 700 }),
  azureButtonStyle: (theme) => ({
    marginTop: theme.spacing(2),
    borderRadius: theme.spacing(4),
    textTransform: "none",
    backgroundColor: "#2F2F2F",
    height: 38,
    fontSize: 12,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    "&:hover": {
      backgroundColor: "#505050",
    },
  }),
  iconStyle: {
    width: "20px",
    height: "20px",
    marginRight: "8px",
  },
});

export default function AzureLoginButton({
  email,
  loading,
  setLoading,
  ...rest
}) {
  const style = useStyle();
  const { instance } = useMsal();
  const dispatch = useDispatch();
  const navigateTo = useNavigation();
  const [isLoading, setIsLoading] = React.useState(false);

  const handleAzureLogin = async () => {
    try {
      // Email validation with regex
      if (email) {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(email)) {
          Notifications.error("Invalid email address format");
          return;
        }
      }

      setIsLoading(true);
      if (setLoading) setLoading(true);

      Notifications.activity("Authenticating with Microsoft...");

      try {
        const response = await instance.loginPopup({
          ...loginRequest,
          prompt: "select_account",
        });

        // Send token to backend for validation
        const { data, error } = await loginWithAzure(response, email);

        if (!error && data) {
          setLocalItem(tokenIdf, JSON.stringify(data));
          dispatch({ type: "SIGNIN_SUCCESS", payload: data });

          Notifications.success("Successfully signed in with Microsoft!");

          setTimeout(() => {
            navigateTo(routes.base);
          }, 800);
        } else {
          Notifications.clear();
          Notifications.error(data?.message ?? "Azure authentication failed");
          setIsLoading(false);
          if (setLoading) {
            setLoading(false);
          }
        }
      } catch (error) {
        // Clear the activity notification
        Notifications.clear();

        // Check if it's a user cancellation error
        if (
          error.name === "PopupCancelledError" ||
          error.message?.includes("popup")
        ) {
          Notifications.error("Microsoft login was cancelled");
        } else {
          Notifications.error(
            "Microsoft authentication failed. Please try again."
          );
        }

        setIsLoading(false);
        if (setLoading) setLoading(false);
      }
    } catch (error) {
      Notifications.clear();
      Notifications.error("Microsoft authentication failed. Please try again.");
      setIsLoading(false);
      if (setLoading) setLoading(false);
    }
  };

  return (
    <Button
      fullWidth
      variant="contained"
      sx={style.azureButtonStyle}
      onClick={handleAzureLogin}
      disabled={loading || isLoading}
      {...rest}
    >
      {!isLoading ? (
        <>
          <MicrosoftIcon style={style.iconStyle} />
          <Typography variant="subtitle1" sx={style.typographyStyle}>
            Sign in with Microsoft
          </Typography>
        </>
      ) : (
        <CircularProgress style={{ color: "white" }} size={25} />
      )}
    </Button>
  );
}
